import type {
  EntityId,
  JsonValue,
  KnowledgeItem,
  UnixTimestamp,
  VersionedEntity,
} from "@note-app/contracts";

import { InvalidStateError, ValidationError } from "./errors.ts";
import {
  assertJsonValue,
  assertUuid,
  assertVersionedEntity,
  CURRENT_SCHEMA_VERSION,
  isUuid,
} from "./validation.ts";

export interface Folder extends VersionedEntity {
  name: string;
  parentId?: EntityId;
  position: number;
}

export interface Tag extends VersionedEntity {
  name: string;
  color?: string;
}

export type RevisionEntityType = "document" | "canvas";
export type RevisionOperation = "save" | "safety-snapshot" | "restore";

export interface RevisionRecord {
  id: EntityId;
  itemId: EntityId;
  entityType: RevisionEntityType;
  operation: RevisionOperation;
  revision: number;
  schemaVersion: number;
  snapshot: JsonValue;
  createdAt: UnixTimestamp;
}

export interface NewKnowledgeItem {
  id: EntityId;
  kind: KnowledgeItem["kind"];
  title: string;
  createdAt: UnixTimestamp;
  icon?: string;
  folderId?: EntityId;
  tags?: readonly EntityId[];
  properties?: Readonly<Record<string, JsonValue>>;
}

export function createKnowledgeItem(input: NewKnowledgeItem): KnowledgeItem {
  assertUuid(input.id);
  validateTitle(input.title);
  if (input.folderId !== undefined) {
    assertUuid(input.folderId, "folderId");
  }
  const tags = [...new Set(input.tags ?? [])];
  tags.forEach((tagId, index) => assertUuid(tagId, `tags[${index}]`));
  assertJsonValue(input.properties ?? {}, "properties");

  const item: KnowledgeItem = {
    id: input.id,
    kind: input.kind,
    title: input.title.trim(),
    tags,
    properties: { ...(input.properties ?? {}) },
    schemaVersion: CURRENT_SCHEMA_VERSION,
    revision: 0,
    createdAt: input.createdAt,
    updatedAt: input.createdAt,
    ...(input.icon === undefined ? {} : { icon: input.icon }),
    ...(input.folderId === undefined ? {} : { folderId: input.folderId }),
  };
  assertKnowledgeItem(item);
  return item;
}

export function assertKnowledgeItem(item: KnowledgeItem): void {
  assertVersionedEntity(item);
  validateTitle(item.title);
  if (item.folderId !== undefined) {
    assertUuid(item.folderId, "folderId");
  }
  const seen = new Set<string>();
  item.tags.forEach((tagId, index) => {
    assertUuid(tagId, `tags[${index}]`);
    if (seen.has(tagId)) {
      throw new ValidationError([
        { path: `tags[${index}]`, message: "must be unique" },
      ]);
    }
    seen.add(tagId);
  });
  assertJsonValue(item.properties, "properties");
}

export function createFolder(input: {
  id: EntityId;
  name: string;
  createdAt: UnixTimestamp;
  parentId?: EntityId;
  position?: number;
}): Folder {
  const folder: Folder = {
    id: input.id,
    name: input.name.trim(),
    position: input.position ?? 0,
    schemaVersion: CURRENT_SCHEMA_VERSION,
    revision: 0,
    createdAt: input.createdAt,
    updatedAt: input.createdAt,
    ...(input.parentId === undefined ? {} : { parentId: input.parentId }),
  };
  assertFolder(folder);
  return folder;
}

export function assertFolder(folder: Folder): void {
  assertVersionedEntity(folder);
  assertName(folder.name, "name");
  if (folder.parentId !== undefined) {
    assertUuid(folder.parentId, "parentId");
    if (folder.parentId === folder.id) {
      throw new ValidationError([
        { path: "parentId", message: "must not refer to the folder itself" },
      ]);
    }
  }
  if (!Number.isSafeInteger(folder.position)) {
    throw new ValidationError([
      { path: "position", message: "must be a safe integer" },
    ]);
  }
}

export function createTag(input: {
  id: EntityId;
  name: string;
  createdAt: UnixTimestamp;
  color?: string;
}): Tag {
  const tag: Tag = {
    id: input.id,
    name: input.name.trim(),
    schemaVersion: CURRENT_SCHEMA_VERSION,
    revision: 0,
    createdAt: input.createdAt,
    updatedAt: input.createdAt,
    ...(input.color === undefined ? {} : { color: input.color }),
  };
  assertTag(tag);
  return tag;
}

export function assertTag(tag: Tag): void {
  assertVersionedEntity(tag);
  assertName(tag.name, "name");
  if (tag.color !== undefined && tag.color.trim().length === 0) {
    throw new ValidationError([
      { path: "color", message: "must not be blank when provided" },
    ]);
  }
}

export function assertRevisionRecord(record: RevisionRecord): void {
  assertUuid(record.id);
  assertUuid(record.itemId, "itemId");
  if (!Number.isSafeInteger(record.revision) || record.revision < 1) {
    throw new ValidationError([
      { path: "revision", message: "must be a positive safe integer" },
    ]);
  }
  if (
    record.operation !== undefined &&
    record.operation !== "save" &&
    record.operation !== "safety-snapshot" &&
    record.operation !== "restore"
  ) {
    throw new ValidationError([
      { path: "operation", message: "must be a supported revision operation" },
    ]);
  }
  if (
    !Number.isSafeInteger(record.createdAt) ||
    record.createdAt < 0
  ) {
    throw new ValidationError([
      {
        path: "createdAt",
        message: "must be a non-negative Unix timestamp in milliseconds",
      },
    ]);
  }
  if (record.schemaVersion !== CURRENT_SCHEMA_VERSION) {
    throw new ValidationError([
      {
        path: "schemaVersion",
        message: `must be ${CURRENT_SCHEMA_VERSION}`,
      },
    ]);
  }
  assertJsonValue(record.snapshot, "snapshot");
}

export function nextRevision(current: number): number {
  if (!Number.isSafeInteger(current) || current < 0) {
    throw new ValidationError([
      {
        path: "revision",
        message: "must be a non-negative safe integer",
      },
    ]);
  }
  if (current === Number.MAX_SAFE_INTEGER) {
    throw new InvalidStateError("Revision has reached Number.MAX_SAFE_INTEGER");
  }
  return current + 1;
}

export function assertCanMoveToTrash(entity: VersionedEntity): void {
  if (entity.deletedAt !== undefined) {
    throw new InvalidStateError(`${entity.id} is already in the trash`);
  }
}

export function assertCanRestore(entity: VersionedEntity): void {
  if (entity.deletedAt === undefined) {
    throw new InvalidStateError(`${entity.id} is not in the trash`);
  }
}

function validateTitle(title: string): void {
  assertName(title, "title");
  if (title.length > 500) {
    throw new ValidationError([
      { path: "title", message: "must be at most 500 characters" },
    ]);
  }
}

function assertName(value: string, path: string): void {
  if (value.trim().length === 0) {
    throw new ValidationError([{ path, message: "must not be blank" }]);
  }
}

export function isEntityId(value: string): value is EntityId {
  return isUuid(value);
}
